<template>
  <head-wrapper />

  <config-wrapper>
    <div
      id="app_v2"
      class="flex flex-col fixed w-screen h-screen"
      v-show="shouldShowApp"
    >
      <NavBarV2
        v-if="shouldShowNavBar"
        class="flex-shrink-0"
        @newItemAdded="handleItemAdded"
      />

      <div class="flex flex-row flex-1 overflow-hidden">
        <SideBarV2
          ref="sideBar"
          v-if="shouldShowSideBar"
          class="flex-shrink-0"
        />

        <div class="flex flex-col flex-1 overflow-hidden rounded-tl-lg router">
          <BannerWrapper v-if="shouldShowBanner" class="flex-shrink-0" />

          <div
            class="flex-1 overflow-auto"
            :class="[
              { text_color: isDarkTheme },
              isFormBuilder ? 'p-0' : 'p-4'
            ]"
          >
            <router-view :key="$route.fullPath" />
          </div>
        </div>
      </div>

      <EmbeddedChatsPanel v-if="shouldShowEmbeddedChats && isChatAppInstalled && isEmbeddedChatsEnabled" />
    </div>

    <BodyWrapper />
    <HuddleInviteDialog v-if="isHuddleEnabled" />
    <HuddlePanel v-if="isHuddleEnabled" />
  </config-wrapper>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { themeTypes } from "@/src/constants/themeConstants.js";
import { registerEventHandlers } from "@/src/services/chats/index.js";
import { checkIfOnboardingStatusAvailable } from "@/src/utils/utils.js";
import { realtimeClient } from "@/src/services/chats/realtimeClient.js";
import { fetchBulkPresence, startHeartbeat, stopHeartbeat } from "@/src/services/chats/presenceService.js";
import WorkspaceService from "@/src/api/WorkspaceService.js";
import eventBus from "@/src/utils/eventBus.js";
import mqttService from "@/src/services/mqtt/mqttService.js";
import NavBarV2 from "@/src/views/layout/NavBarV2.vue";
import SideBarV2 from "@/src/views/layout/SideBarV2.vue";
import BannerWrapper from "@/src/views/layout/BannerWrapper.vue";
import HeadWrapper from "@/src/components/layout_components/HeadWrapper.vue";
import BodyWrapper from "@/src/components/layout_components/BodyWrapper.vue";
import ConfigWrapper from "@/src/components/layout_components/ConfigWrapper.vue";
import HuddleInviteDialog from "@/src/components/projects_components/chats/huddle/HuddleInviteDialog.vue";
import HuddlePanel from "@/src/components/projects_components/chats/huddle/HuddlePanel.vue";
import EmbeddedChatsPanel from "@/src/components/projects_components/chats/EmbeddedChatsPanel.vue";
import { isAppInstalled } from "@/src/utils/appStoreUtils.js";
import DashboardService from "@/src/api/DashboardService.js";
import { useAuthStore } from "@/src/composables/useAuthStore.js";

const route = useRoute();
const router = useRouter();
const store = useStore();
const authStore = useAuthStore();

const sideBar = ref(null);
const isChatAppInstalled = ref(false);
const isEmbeddedChatsEnabled = ref(false);

const isHuddleEnabled = computed(() => !!localStorage.getItem("huddle"));

const shouldShowApp = computed(() => {
  const isPublicRoute = route.meta.public === true;
  return isPublicRoute || checkIfOnboardingStatusAvailable();
});

const shouldShowSideBar = computed(() => {
  const isPublicRoute = route.meta.public === true;
  if (isPublicRoute) return false;
  return !(
    route.meta.hideSidebar === undefined || route.meta.hideSidebar === true
  );
});

const shouldShowNavBar = computed(() => {
  const isPublicRoute = route.meta.public === true;
  if (isPublicRoute) return true;
  return !(
    route.meta.hideNavbar === undefined || route.meta.hideNavbar === true
  );
});

const shouldShowEmbeddedChats = computed(() => {
  const isPublicRoute = route.meta.public === true;
  if (isPublicRoute || !authStore.isAuthenticated.value) return false;
  return shouldShowNavBar.value;
});

const shouldShowBanner = computed(() => {
  const isPublicRoute = route.meta.public === true;
  return !isPublicRoute;
});

const effectiveTheme = computed(() => {
  const isPublicRoute = route.meta.public === true;
  const isAuthenticated = authStore.isAuthenticated.value;
  
  if (isPublicRoute && !isAuthenticated) {
    return themeTypes.LIGHT;
  }
  return store.getters["theme/getEffectiveTheme"];
});

const isDarkTheme = computed(() => {
  return effectiveTheme.value === themeTypes.DARK;
});

const isFormBuilder = computed(() => {
  return route.name === "form-edit";
});

const handleItemAdded = ({ type }) => {
  if (route.name === `${type}`) {
    eventBus.emit(`refresh-${type}`);
  }
};

watch(
  () => route.path,
  (newPath, oldPath) => {
    if (
      newPath.startsWith("/market") &&
      oldPath &&
      !oldPath.startsWith("/market")
    ) {
      store.commit("appStore/SET_LAST_ROUTE", oldPath);
    }
  },
);

watch(
  () => [route.meta.public, authStore.isAuthenticated.value],
  ([isPublic, isAuthenticated]) => {
    if (isPublic && !isAuthenticated) {
      document.documentElement.setAttribute("data-theme", "LIGHT");
    }
  },
  { immediate: true }
);

onMounted(async () => {
  registerEventHandlers();

  await router.isReady();
  const isPublicRoute = route.meta.public === true;
  const isAuthenticated = authStore.isAuthenticated.value;

  if (isPublicRoute && !isAuthenticated) {
    document.documentElement.setAttribute("data-theme", "LIGHT");
  } else {
    try {
      await Promise.all([
        store.dispatch("onboarding/fetchStatus"),
        store.dispatch("getStarted/fetchStatus"),
        store.dispatch("billing/getBillingDetails"),
        mqttService.initialize("default"),
      ]);
      
      const workspaceId = WorkspaceService.getCurrentWorkspaceId();
      const currentUserId = store.getters["auth/userData"]?.id || localStorage.getItem("userId");

      if (workspaceId) {
        if (currentUserId) {
          await realtimeClient.setWorkspaceAndUser(workspaceId, currentUserId);
          await realtimeClient.subscribeToUserSidebar(currentUserId);
          if (localStorage.getItem("chat_presence")) {
            await realtimeClient.subscribeToPresence();
            fetchBulkPresence();
            startHeartbeat();
          }
        } else {
          await realtimeClient.setWorkspaceAndUser(workspaceId, null);
        }

        // Fetch groups and store member IDs, then subscribe to huddle wildcard topics
        if (localStorage.getItem("huddle")) {
          try {
            const groupsResult = await store.dispatch("chats/fetchGroups");
            if (groupsResult?.success && groupsResult.groups?.length) {
              const memberGroups = groupsResult.groups.filter((g) => {
                if (g.is_member === true) return true;
                // DMs always imply membership
                if (g.is_dm || g.type === "dm" || g.type === "self_dm" || g.type === "group_dm" || g.type === "private") return true;
                return false;
              });
              const memberIds = memberGroups.map((g) => String(g.id));
              store.commit("huddles/SET_MEMBER_GROUP_IDS", memberIds);
            }
            await realtimeClient.subscribeToHuddleTopics();
          } catch (huddleSubErr) {
            console.warn("Failed to subscribe to huddle topics:", huddleSubErr);
          }
        }
      }
    } catch (error) {
      console.error("Error initializing app data:", error);
    }

    try {
      const [chatInstalled, featuresResponse] = await Promise.all([
        isAppInstalled("chat").catch(() => false),
        DashboardService.fetchEnabledFeatures().catch(() => ({})),
      ]);
      isChatAppInstalled.value = chatInstalled;
      isEmbeddedChatsEnabled.value = !!featuresResponse?.features?.embedded_chats_enabled;
    } catch (error) {
      console.error("Error checking chat app installation:", error);
      isChatAppInstalled.value = false;
      isEmbeddedChatsEnabled.value = false;
    }
  }

  const lushaElement = document.getElementById("LU__root_ele");
  if (lushaElement) {
    lushaElement.style.display = "none";
  }

  eventBus.on("mqtt-reconnected", handlePresenceReconnect);
});

async function handlePresenceReconnect() {
  if (localStorage.getItem("chat_presence")) {
    fetchBulkPresence();
  }
}

onBeforeUnmount(() => {
  stopHeartbeat();
  eventBus.off("mqtt-reconnected", handlePresenceReconnect);
});
</script>
